import { useState } from 'react';
import { XMarkIcon, PlusIcon, PlayIcon, MicrophoneIcon } from '@heroicons/react/24/outline';
import { IUserModel } from '@packages/global';
import { useTranslation } from 'react-i18next';
import ImageUpload from '../../../../common/ImageUpload';
import AudioUpload from '../../../../common/AudioUpload';
import MediaPreviewModal from '../../../../common/MediaPreviewModal';

interface ReferenceMediaSectionProps {
  referenceImages: string[];
  referenceAudio: string;
  audioSupported: boolean;
  isDisabled: boolean;
  projectId: string;
  episodeId: string;
  models: IUserModel[];
  showImageUpload: boolean;
  showAudioUpload: boolean;
  onImageUploadToggle: (show: boolean) => void;
  onAudioUploadToggle: (show: boolean) => void;
  onAddImage: (url: string) => void;
  onRemoveImage: (index: number) => void;
  onAudioChange: (url: string) => void;
  audioDuration?: number | null;
  isAudioTooShort?: boolean;
  selectedModel?: IUserModel | null;
  maxImageCount?: number;
  showAudioSection?: boolean;
}

export default function ReferenceMediaSection({
  referenceImages,
  referenceAudio,
  audioSupported,
  isDisabled,
  projectId,
  episodeId,
  models,
  showImageUpload,
  showAudioUpload,
  onImageUploadToggle,
  onAudioUploadToggle,
  onAddImage,
  onRemoveImage,
  onAudioChange,
  audioDuration,
  isAudioTooShort,
  selectedModel,
  maxImageCount = 4,
  showAudioSection = true
}: ReferenceMediaSectionProps) {
  const { t } = useTranslation('editor');
  const canAddMoreImages = referenceImages.length < maxImageCount;
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const modelMaxImages = selectedModel?.imageInputCount || maxImageCount;
  const effectiveMaxImages = Math.min(maxImageCount, modelMaxImages);

  return (
    <div className="px-4 pt-3">
      <div className="flex gap-2 items-center justify-between">
        {/* 参考图片 */}
        <div className="flex gap-2 relative">
          {Array.from({ length: maxImageCount }).map((_, index) => {
            const imageUrl = referenceImages[index];
            const isSupported = index < effectiveMaxImages;
            const isExcessive = index >= effectiveMaxImages && imageUrl;

            return (
              <div key={index} className="relative">
                {imageUrl ? (
                  <div className="relative group">
                    <img
                      src={imageUrl}
                      alt={t('videoGenForm.refImageAlt', { index: index + 1 })}
                      className={`w-12 h-12 object-cover rounded-lg border cursor-pointer transition-opacity ${isSupported
                        ? 'border-gray-700 hover:opacity-80'
                        : 'border-red-500/50 opacity-50'
                        }`}
                      onClick={() => isSupported && setPreviewImage(imageUrl)}
                    />
                    <button
                      type="button"
                      onClick={() => onRemoveImage(index)}
                      disabled={isDisabled}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-50"
                    >
                      <XMarkIcon className="h-2.5 w-2.5" />
                    </button>
                    {isExcessive && (
                      <div className="absolute inset-0 bg-red-900/50 rounded-lg flex items-center justify-center">
                        <XMarkIcon className="h-6 w-6 text-red-300" />
                      </div>
                    )}
                    {isSupported && maxImageCount === 2 && (
                      <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 bg-black/70 text-white text-xs text-center rounded-b-lg">
                        {index === 0 ? t('videoGenForm.keyframeStart') : t('videoGenForm.keyframeEnd')}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => isSupported && onImageUploadToggle(true)}
                    disabled={isDisabled || !isSupported}
                    className={`w-12 h-12 rounded-lg flex items-center justify-center transition-colors border-2 border-dashed ${isSupported
                      ? 'bg-gray-800 hover:bg-gray-700 border-gray-600 disabled:opacity-50'
                      : 'bg-gray-900/50 border-gray-800 cursor-not-allowed opacity-30'
                      }`}
                    title={isSupported ? t('videoGenForm.uploadRefImage', { index: index + 1 }) : t('videoGenForm.modelNotSupported')}
                  >
                    <PlusIcon className={`h-5 w-5 ${isSupported ? 'text-gray-400' : 'text-gray-700'}`} />
                  </button>
                )}
              </div>
            );
          })}

          {showImageUpload && canAddMoreImages && (
            <div className="absolute bottom-full left-0 mb-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-20 p-3 min-w-[280px]">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-gray-400">
                  {t('videoGenForm.refImageCount', { current: referenceImages.filter((_, i) => i < effectiveMaxImages).length, max: effectiveMaxImages })}
                </span>
                <button
                  onClick={() => onImageUploadToggle(false)}
                  className="text-gray-500 hover:text-gray-300"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
              <ImageUpload
                projectId={projectId}
                episodeId={episodeId}
                onUploadSuccess={onAddImage}
                models={models}
              />
            </div>
          )}
        </div>

        {showAudioSection && (
          <div className="relative">
            {referenceAudio ? (
              <div className={`relative w-12 h-12 rounded-lg flex items-center justify-center group ${audioSupported ? 'bg-blue-800/30' : 'bg-gray-700/50'}`}>
                <audio ref={(audio) => {
                  if (audio) audio.src = referenceAudio;
                }} className="hidden" />
                <button
                  onClick={(e) => {
                    const audio = e.currentTarget.parentElement?.querySelector('audio') as HTMLAudioElement;
                    if (audio) audio.paused ? audio.play() : audio.pause();
                  }}
                  disabled={isDisabled}
                  className={`transition-colors disabled:opacity-50 ${audioSupported ? 'text-blue-400 hover:text-blue-300' : 'text-gray-500'}`}
                >
                  <PlayIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => onAudioChange('')}
                  disabled={isDisabled}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-50"
                >
                  <XMarkIcon className="h-2.5 w-2.5" />
                </button>
                {!audioSupported && (
                  <div className="absolute inset-0 bg-gray-900/50 rounded-lg flex items-center justify-center">
                    <div className="w-6 h-0.5 bg-red-500 rotate-45 absolute"></div>
                    <div className="w-6 h-0.5 bg-red-500 -rotate-45 absolute"></div>
                  </div>
                )}
                {audioDuration !== null && audioDuration !== undefined && (
                  <div className={`absolute bottom-0 left-0 right-0 px-0.5 py-0.5 rounded-b-lg text-center ${isAudioTooShort ? 'bg-red-900/80 text-red-300' : 'bg-black/70 text-white'}`} style={{ fontSize: '10px' }}>
                    {audioDuration.toFixed(1)}s
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => onAudioUploadToggle(true)}
                disabled={isDisabled}
                className={`w-12 h-12 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 ${audioSupported ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-700/50 cursor-not-allowed'}`}
                title={audioSupported ? t('videoGenForm.addRefAudio') : t('videoGenForm.audioNotSupported')}
              >
                <MicrophoneIcon className={`h-5 w-5 ${audioSupported ? 'text-gray-400' : 'text-gray-600'}`} />
                {!audioSupported && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-6 h-0.5 bg-red-500 rotate-45 absolute"></div>
                    <div className="w-6 h-0.5 bg-red-500 -rotate-45 absolute"></div>
                  </div>
                )}
              </button>
            )}

            {showAudioUpload && audioSupported && (
              <div className="absolute bottom-full right-0 mb-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-20 p-3 min-w-[280px]">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-gray-400">{t('videoGenForm.addRefAudio')}</span>
                  <button
                    onClick={() => onAudioUploadToggle(false)}
                    className="text-gray-500 hover:text-gray-300"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
                <AudioUpload
                  showAudioSelector={true}
                  showToneSelector={false}
                  value=""
                  onUploadSuccess={(url) => {
                    onAudioChange(url);
                    onAudioUploadToggle(false);
                  }}
                  placeholder={t('videoGenForm.uploadRefAudio')}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <MediaPreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        type="image"
        src={previewImage || ''}
        alt={t('videoGenForm.refImagePreview')}
      />
    </div>
  );
}
